import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "./qimen-dunjia-pass-style.css";
import useIsMobile from "../../../../hooks/useIsMobile";
import { PLATES } from "../qimen-dunjia-constants";
import FunctionService from "../../../../service/function-service";
import ProductService from "../../../../service/product-service";
import Loading from "../../../dialog/loading/loading";

//奇門遁甲 — pass 頁（過程頁面）
//  3 步驟：選事件 → 選盤別 → 選日期
//  完成 → 跳 /qimen-dunjia/result 並把 { event, plate, date } 帶過去
const QimenDunjiaPassPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const presetEvent = location.state?.presetEvent;

  //避開全站 nav 遮住內容
  const [navHeight, setNavHeight] = useState(0);
  useEffect(() => {
    const nav = document.querySelector(
      isMobile ? ".mobile-dashboard nav" : ".dashboard nav",
    );
    setNavHeight(nav ? nav.offsetHeight : 0);
  }, [isMobile]);

  //categories 每筆內嵌 events：{ ...category, events: [...] }
  const [categories, setCategories] = useState([]);
  //Step 1 當前選的類別 tab（預設等類別載完後設第一個）
  const [activeCategory, setActiveCategory] = useState(null);

  useEffect(() => {
    FunctionService.getQimenMeta()
      .then((res) => setCategories(res.data.data?.categories || []))
      .catch((e) => console.error("[QimenPass] 拉 meta 失敗:", e));
  }, []);

  //只顯示「有事件」的類別 tab（events 陣列有東西才留）
  const visibleCategories = categories.filter((cat) => cat.events?.length > 0);

  //categories 到位後，把 activeCategory 鎖到「第一個有事件」的類別
  //  避免預設指到空類別（這時 tab 被藏起來、畫面只剩 empty state）
  useEffect(() => {
    if (!visibleCategories.length) return;
    const stillVisible = visibleCategories.some(
      (c) => c.categoryId === activeCategory,
    );
    if (!stillVisible) {
      setActiveCategory(visibleCategories[0].categoryId);
    }
  }, [visibleCategories, activeCategory]);

  //當前 tab 對應的事件清單（直接從嵌套結構拿）
  const eventsInActiveCategory =
    categories.find((c) => c.categoryId === activeCategory)?.events || [];

  //從 enter 頁帶 presetEvent 進來 → 直接從 Step 2 開始（盤別不預選，讓使用者自己挑）
  const [step, setStep] = useState(presetEvent ? 2 : 1);
  const [selectedEvent, setSelectedEvent] = useState(presetEvent ?? null);
  const [selectedPlate, setSelectedPlate] = useState(null);
  //時盤用 selectedDate（單一天）；日盤用 selectedRange（區間 {start, end}）
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedRange, setSelectedRange] = useState({
    start: null,
    end: null,
  });

  //日盤可選窗口：今天 ~ 今天+6（共 7 天）
  const dayWindow = (() => {
    const min = new Date();
    min.setHours(0, 0, 0, 0);
    const max = new Date(min);
    max.setDate(max.getDate() + 6);
    return { min, max };
  })();
  //打 F-3 中的 loading 狀態（12 時辰 / N 日要算盤，可能 1~3 秒）
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  //—— 金流 ——
  //isPolling：付款分頁切回來，正在輪詢綠界 callback 確認
  //  orderID 從 /checkout 導回時透過 location.state.pendingOrderID 帶過來
  const [isPolling, setIsPolling] = useState(false);
  const pendingOrderIdRef = useRef(location.state?.pendingOrderID || null);
  const pollingRef = useRef(false);

  //—— Step 1: 選事件 → 進 Step 2（盤別由使用者在 Step 2 自己選）——
  const handlePickEvent = (ev) => {
    setSelectedEvent(ev);
    setStep(2);
  };

  //—— Step 2: 選盤別 → 進 Step 3（每次重選盤別都把日期清空，避免殘留）——
  const handlePickPlate = (p) => {
    setSelectedPlate(p);
    setSelectedDate(null);
    setSelectedRange({ start: null, end: null });
    setStep(3);
  };

  //—— Step 3: 選日期 → 導 /checkout（選發票、送出）——
  //  /checkout 建單 + 跳綠界 + 導回本頁，本頁 visibilitychange 開始 poll
  const handleSubmitDate = () => {
    if (submitting || isPolling) return;
    //資料完整性檢查
    if (selectedPlate.id === "1" && !selectedDate) return;
    if (
      selectedPlate.id === "2" &&
      (!selectedRange.start || !selectedRange.end)
    )
      return;
    setErrorMsg("");

    const fmt = (d) =>
      `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}`;

    const payload = {
      eventId: selectedEvent.eventId,
      plateType: selectedPlate.id,
    };
    if (selectedPlate.id === "1") {
      payload.date = fmt(selectedDate);
    } else {
      payload.startDate = fmt(selectedRange.start);
      payload.endDate = fmt(selectedRange.end);
    }

    navigate("/checkout", {
      state: {
        planID: "digital_qimen",
        itemName: "奇門吉時查詢",
        amount: 50,
        payload,
        returnPath: location.pathname,
      },
    });
  };

  //付款分頁切回來時：輪詢「綠界 callback 是否已被後端確認 + report 是否 ready」
  //  ready 後直接導 /qimen-dunjia/result 並帶報告資料
  useEffect(() => {
    const startPolling = async (orderId) => {
      if (pollingRef.current) return;
      pollingRef.current = true;
      setIsPolling(true);

      const MAX_TRIES = 48; //× 2.5 秒 = 2 分鐘
      const INTERVAL_MS = 2500;

      for (let i = 0; i < MAX_TRIES; i++) {
        if (pendingOrderIdRef.current !== orderId) break;
        try {
          const res = await ProductService.getQimenReportByOrder(orderId);
          const d = res.data?.data;
          //奇門遁甲是同步算的，paid + ready 幾乎同時來
          if (res.data?.status && d?.paid && d?.ready && d?.report) {
            pendingOrderIdRef.current = null;
            pollingRef.current = false;
            setIsPolling(false);
            navigate("/qimen-dunjia/result", {
              state: {
                event: d.report.event,
                plate: d.report.plate,
                date: d.report.date,
                results: d.report.results,
                dictionary: d.report.dictionary, //八門解析 / 步驟 / FAQ / 兇格（後端出）
              },
            });
            return;
          }
          //付款已確認但報告產生失敗 → 跳書房讓使用者看狀態 / 聯絡客服
          if (res.data?.status && d?.failed) {
            pendingOrderIdRef.current = null;
            pollingRef.current = false;
            setIsPolling(false);
            navigate("/study-room");
            return;
          }
        } catch (e) {
          console.log(e);
        }
        await new Promise((r) => setTimeout(r, INTERVAL_MS));
      }
      //輪詢超時：付款 callback 沒回來
      pendingOrderIdRef.current = null;
      pollingRef.current = false;
      setIsPolling(false);
      navigate("/study-room");
    };

    const onVisible = () => {
      if (
        document.visibilityState === "visible" &&
        pendingOrderIdRef.current &&
        !pollingRef.current
      ) {
        startPolling(pendingOrderIdRef.current);
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [navigate]);

  const handleBack = () => {
    if (step === 1) {
      navigate("/qimen-dunjia"); //回 enter
    } else {
      setStep(step - 1);
    }
  };

  return (
    <div className="qimen-dunjia-page">
      {/* 蓋房子 loading：付款分頁切回後 polling 到報告 ready 才消失 */}
      {isPolling && <Loading />}

      <main className="mobile-main" style={{ paddingTop: navHeight }}>
        <div className="wizard">
          {/* 頂部 header */}
          <div className="wizard-header">
            {step > 1 ? (
              <button className="back-btn" onClick={handleBack}>
                <i className="bi bi-chevron-left"></i> 上一步
              </button>
            ) : (
              <div className="back-placeholder"></div>
            )}

            <h2 className="wizard-title">
              {step === 1 && "奇門遁甲"}
              {step === 2 && "選擇盤別"}
              {step === 3 && "選擇時間"}
            </h2>

            {/* 右側 placeholder 維持 wizard-title 居中 */}
            <div className="back-placeholder"></div>
          </div>

          {/* 步驟指示器 */}
          <div className="step-indicator">
            <div
              className={`step ${step >= 1 ? "active" : ""} ${step > 1 ? "done" : ""}`}
            >
              {step > 1 ? <i className="bi bi-check"></i> : "1"}
              <span>選事件</span>
            </div>
            <div className="step-line"></div>
            <div
              className={`step ${step >= 2 ? "active" : ""} ${step > 2 ? "done" : ""}`}
            >
              {step > 2 ? <i className="bi bi-check"></i> : "2"}
              <span>選盤別</span>
            </div>
            <div className="step-line"></div>
            <div className={`step ${step >= 3 ? "active" : ""}`}>
              3<span>選時間</span>
            </div>
          </div>

          {/* Step 1: 選事件 — 加類別 tabs */}
          {step === 1 && (
            <div className="step-content step-event">
              <h3 className="section-title">請選擇您的事件</h3>

              {/* 類別 tabs：橫向 scroll on mobile；空類別不顯示 */}
              <div className="category-tabs">
                {visibleCategories.map((cat) => (
                  <button
                    key={cat.categoryId}
                    className={`category-tab ${activeCategory === cat.categoryId ? "active" : ""}`}
                    onClick={() => setActiveCategory(cat.categoryId)}
                  >
                    <i className={`bi ${cat.icon}`}></i>
                    <span>{cat.label}</span>
                  </button>
                ))}
              </div>

              <div className="event-grid">
                {eventsInActiveCategory.length === 0 ? (
                  <p className="empty-category">此類別暫無事件</p>
                ) : (
                  eventsInActiveCategory.map((ev) => (
                    <button
                      key={ev.eventId}
                      className={`event-card color-${ev.color}`}
                      onClick={() => handlePickEvent(ev)}
                    >
                      <div className="icon-wrap">
                        <i className={`bi ${ev.icon}`}></i>
                      </div>
                      <div className="event-text">
                        <p className="event-label">
                          {/* web 一律單行；mobile 4 字才拆 2/2 兩行 */}
                          {isMobile && ev.label.length === 4 ? (
                            <>
                              {ev.label.slice(0, 2)}
                              <br />
                              {ev.label.slice(2)}
                            </>
                          ) : (
                            ev.label
                          )}
                        </p>
                        <p className="event-recommend">
                          推薦：
                          {ev.recommendedPlate === "time" ? "時盤" : "日盤"}
                        </p>
                      </div>
                      <i className="bi bi-chevron-right chevron"></i>
                    </button>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Step 2: 選盤別 */}
          {step === 2 && selectedEvent && (
            <div className="step-content step-plate">
              <div className="event-tag">
                <div className={`icon-wrap small color-${selectedEvent.color}`}>
                  <i className={`bi ${selectedEvent.icon}`}></i>
                </div>
                <span>{selectedEvent.label}</span>
              </div>
              <h3 className="section-title">選擇使用哪種盤</h3>

              <div className="plate-list">
                {PLATES.map((p) => {
                  const isRecommended = selectedEvent.recommendedPlate === p.id;
                  const isSelected = selectedPlate?.id === p.id;
                  return (
                    <button
                      key={p.id}
                      className={`plate-card ${isSelected ? "selected" : ""}`}
                      onClick={() => handlePickPlate(p)}
                    >
                      <div className="icon-wrap">
                        <i className={`bi ${p.icon}`}></i>
                      </div>
                      <div className="plate-text">
                        <p className="plate-label">
                          {p.label}
                          {isRecommended && (
                            <span className="recommend-badge">推薦</span>
                          )}
                        </p>
                        <p className="plate-desc">{p.description}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Step 3: 選日期（時盤=單日；日盤=今天起 7 天內的區間） */}
          {step === 3 && (
            <div className="step-content step-date">
              <h3 className="section-title">
                {selectedPlate?.id === "2" ? "選擇日期區間" : "選擇查詢日期"}
              </h3>
              <p className="section-sub">
                {selectedPlate?.id === "2"
                  ? "點兩次選範圍：起始日 → 結束日（最多 7 天內）"
                  : "將顯示該日最佳時辰"}
              </p>

              {selectedPlate?.id === "2" ? (
                <SimpleCalendar
                  mode="range"
                  value={selectedRange}
                  onChange={setSelectedRange}
                  minDate={dayWindow.min}
                  maxDate={dayWindow.max}
                />
              ) : (
                <SimpleCalendar
                  mode="single"
                  value={selectedDate}
                  onChange={setSelectedDate}
                />
              )}

              {/* 日盤：顯示目前已選區間 */}
              {selectedPlate?.id === "2" && selectedRange.start && (
                <p className="range-hint">
                  {selectedRange.end
                    ? `已選 ${
                        Math.round(
                          (selectedRange.end - selectedRange.start) / 86400000,
                        ) + 1
                      } 天（${selectedRange.start.getMonth() + 1}/${selectedRange.start.getDate()} ~ ${selectedRange.end.getMonth() + 1}/${selectedRange.end.getDate()}）`
                    : `起始日：${selectedRange.start.getMonth() + 1}/${selectedRange.start.getDate()}，請再點一個結束日`}
                </p>
              )}

              {errorMsg && <p className="error-msg">{errorMsg}</p>}

              <button
                className="primary-btn"
                disabled={
                  submitting ||
                  (selectedPlate?.id === "2"
                    ? !(selectedRange.start && selectedRange.end)
                    : !selectedDate)
                }
                onClick={handleSubmitDate}
              >
                {submitting ? (
                  <>
                    <i className="bi bi-arrow-repeat spinning"></i> 查詢中…
                  </>
                ) : (
                  <>
                    <i className="bi bi-stars"></i> 查看吉時結果
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

//—— 行內月曆 ——
//  mode: "single" | "range"
//    - single: value = Date | null         → onChange(Date)
//    - range:  value = { start, end }      → onChange({start, end})
//  minDate / maxDate: 可選日期窗口（含端點），超出就 disabled
//  range 行為：第一下選 start，第二下選 end；若已完成（有 end）再點則重新開始
const SimpleCalendar = ({
  mode = "single",
  value,
  onChange,
  minDate,
  maxDate,
}) => {
  const today = new Date();
  const refDate =
    mode === "range" ? (value?.start ?? today) : (value ?? today);

  const [viewYear, setViewYear] = useState(refDate.getFullYear());
  const [viewMonth, setViewMonth] = useState(refDate.getMonth());

  const firstDay = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const weekdays = ["日", "一", "二", "三", "四", "五", "六"];
  const monthName = [
    "一月",
    "二月",
    "三月",
    "四月",
    "五月",
    "六月",
    "七月",
    "八月",
    "九月",
    "十月",
    "十一月",
    "十二月",
  ][viewMonth];

  //上/下月導覽：被 min/max 月份夾住，超出就 disable
  const minYM = minDate
    ? minDate.getFullYear() * 12 + minDate.getMonth()
    : -Infinity;
  const maxYM = maxDate
    ? maxDate.getFullYear() * 12 + maxDate.getMonth()
    : Infinity;
  const currYM = viewYear * 12 + viewMonth;
  const canGoPrev = currYM > minYM;
  const canGoNext = currYM < maxYM;

  const goPrev = () => {
    if (!canGoPrev) return;
    if (viewMonth === 0) {
      setViewYear(viewYear - 1);
      setViewMonth(11);
    } else setViewMonth(viewMonth - 1);
  };
  const goNext = () => {
    if (!canGoNext) return;
    if (viewMonth === 11) {
      setViewYear(viewYear + 1);
      setViewMonth(0);
    } else setViewMonth(viewMonth + 1);
  };

  const sameDay = (a, b) =>
    a &&
    b &&
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

  const cellDate = (d) => new Date(viewYear, viewMonth, d);

  const isDisabled = (d) => {
    if (!d) return true;
    const dt = cellDate(d);
    if (minDate && dt < minDate) return true;
    if (maxDate && dt > maxDate) return true;
    return false;
  };

  //single-mode 選中
  const isSingleSelected = (d) =>
    mode === "single" && d && sameDay(cellDate(d), value);

  //range-mode 三種狀態
  const getRangeState = (d) => {
    if (mode !== "range" || !d) return {};
    const dt = cellDate(d);
    const { start, end } = value ?? {};
    return {
      isStart: sameDay(dt, start),
      isEnd: sameDay(dt, end),
      inRange: start && end && dt > start && dt < end,
    };
  };

  const handlePick = (d) => {
    if (!d || isDisabled(d)) return;
    const dt = cellDate(d);

    if (mode === "single") {
      onChange(dt);
      return;
    }

    //range mode
    const { start, end } = value ?? {};
    if (!start || (start && end)) {
      //還沒選 / 已選完一輪 → 重新從這天開始
      onChange({ start: dt, end: null });
    } else {
      //有 start、沒 end → 這次設 end（若早於 start 就 swap）
      if (dt < start) {
        onChange({ start: dt, end: start });
      } else {
        onChange({ start, end: dt });
      }
    }
  };

  const cells = [
    ...Array(firstDay).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];

  return (
    <div className="calendar">
      <div className="cal-header">
        <span className="cal-title">
          {monthName} {viewYear}
          <i className="bi bi-chevron-right"></i>
        </span>
        <div className="cal-nav">
          <button onClick={goPrev} disabled={!canGoPrev}>
            <i className="bi bi-chevron-left"></i>
          </button>
          <button onClick={goNext} disabled={!canGoNext}>
            <i className="bi bi-chevron-right"></i>
          </button>
        </div>
      </div>

      <div className="cal-weekdays">
        {weekdays.map((w) => (
          <div key={w} className="cal-weekday">
            {w}
          </div>
        ))}
      </div>

      <div className="cal-grid">
        {cells.map((d, i) => {
          const disabled = isDisabled(d);
          const single = isSingleSelected(d);
          const { isStart, isEnd, inRange } = getRangeState(d);
          const cls = [
            "cal-cell",
            !d ? "empty" : "",
            disabled && d ? "disabled" : "",
            single ? "selected" : "",
            isStart ? "range-start" : "",
            isEnd ? "range-end" : "",
            inRange ? "in-range" : "",
          ]
            .filter(Boolean)
            .join(" ");

          return (
            <div key={i} className={cls} onClick={() => handlePick(d)}>
              {d}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default QimenDunjiaPassPage;
